import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  getShowCount,
  initialUpperCase,
  sentimentType,
  uniqueArray,
} from '../../utils';
import { languageNumValueMap } from '../FilterBoxCommon/constant';
import { Bubble } from 'tea-component';
import './index.scss';
import { mergeSameKeyword } from '@op/utils/keyword';
import { useIntl } from 'umi';
import BubbleCommon, { selectedKeywordTipsText } from '../BubbleCommon';
import { useGetSingePageSourceRule } from '@op/hooks/useGetPageSourceRule';
export default function KeyWordCommon({
  keywords,
  language,
  stabled = false,
  keywordChange = () => {},
  autoSelectFirst = false,
  commentLoading = false,
  tableTotal = 0,
  defaultSelectedKeyword = [],
  needBrandHealth = false,
}: {
  keywords: Array<any>;
  language: any;
  stabled?: boolean;
  commentLoading?: boolean;
  keywordChange?: (keywords: {
    parent: Array<string>;
    children: Array<string>;
    keywordRender: {
      keyword: [];
      keyword_cn: [];
      keyword_en: [];
    };
  }) => void;
  autoSelectFirst?: boolean;
  tableTotal?: number;
  defaultSelectedKeyword?: Array<string>;
  needBrandHealth?: boolean;
}) {
  // stabled 不需要hover交互时 样式类名stabled

  const { formatMessage } = useIntl();

  const langIds = useMemo(() => {
    return [languageNumValueMap[language]];
  }, [language]);

  /**
   * 获取标签文本
   */
  const getTagText = (v: any) => {
    let { keyword, keyword_en, keyword_cn } = v || {};
    const map: any = {
      0: keyword,
      1: keyword_en,
      2: keyword_cn,
    };
    // 不是数组就直接返回了
    if (!Array.isArray(langIds)) {
      return map[langIds];
    }
    // 如果只有一个，后续版本更改不一定是原文，所以还是使用map去找
    if (langIds.length === 1) {
      return map[langIds[0]];
    }
    // 如果是两个元素，并且有一个是原文的话，那么其中选择的语言作为展示语言
    if (langIds.length === 2 && langIds.includes(0)) {
      return langIds.filter((id) => id !== 0).map((id) => map[id]);
    }
    // 如果都不是，那么就是语言全选，所以就是英文 + 中文
    return keyword_en + ' | ' + keyword_cn;
  };

  const tempKeyword = useMemo(() => {
    let localKeywords = JSON.parse(JSON.stringify(keywords)) || [];
    if (langIds && !Array.isArray(langIds)) {
      localKeywords = mergeSameKeyword(localKeywords, langIds);
    }
    if (langIds && Array.isArray(langIds)) {
      const ids = langIds.filter((id) => id !== 0);
      ids.forEach((id) => {
        localKeywords = mergeSameKeyword(localKeywords, id);
      });
    }
    return localKeywords;
  }, [keywords, langIds]);

  const [selectKeyword, setSelectKeyword] = useState<Array<any>>([]);

  useEffect(() => {
    if (defaultSelectedKeyword.length > 0) {
      const selected = (tempKeyword || [])?.filter((item: any) =>
        defaultSelectedKeyword.includes(item.keyword),
      );
      setSelectKeyword(selected);
    }
  }, [tempKeyword]);

  const selectKeywordInfo = useMemo(() => {
    return selectKeyword.map((item) => item.keyword);
  }, [selectKeyword]);

  const getBackKeyword = useCallback((keywords: Array<any>) => {
    const parent: Array<string> = [];
    const children: Array<string> = [];

    const list = getAllChildList(keywords);
    const keywordRender = getClassKeywordList(list);

    keywords.forEach((item) => {
      parent.push(item.keyword);

      if (Array.isArray(item?.child)) {
        item?.child.forEach((info: any) => {
          children.push(info.keyword);
        });
      }
    });

    return { parent, children, keywordRender };
  }, []);

  const getKeywordText = (keywordTextList: Array<string>, maxIndex: number) => {
    const orText = formatMessage({ id: 'or' });

    if (keywordTextList.length <= maxIndex) {
      return `'${keywordTextList.join(`' ${orText} '`)}'`;
    }

    return `'${keywordTextList
      .slice(0, maxIndex - 1)
      .join(`' ${orText} '`)}' ...${orText} '${keywordTextList
      .slice(-1)
      .join()}'`;
  };

  const getClassKeywordList = (list: Array<any>, fun?: (item: any) => void) => {
    const bubbleText: { [key: string]: any } = {
      keyword: [],
      keyword_cn: [],
      keyword_en: [],
    };
    const keywordKeys = Object.keys(bubbleText);

    list?.forEach((item) => {
      keywordKeys?.forEach((key) => {
        bubbleText[key].push(initialUpperCase(item[key]));
      });
      fun?.(item);
    });

    return bubbleText;
  };

  const bubble = (p: any, keywordMap: { [key: string]: any }) => {
    const titleMap: any = {
      keyword: 'Related keywords',
      keyword_en: 'English Translation',
      keyword_cn: 'Chinese Translation',
    };
    const elementList: Array<JSX.Element> = [];
    for (const key in keywordMap) {
      const element = uniqueArray<string>(keywordMap[key]);

      if (element?.length > 0) {
        const title = titleMap[key];
        elementList.push(
          <Fragment key={key}>
            <div style={key === 'origin' ? {} : { marginTop: 10 }}>
              {formatMessage({ id: title })}:
            </div>
            {element?.map((item: string, index: number) => {
              return (
                <span className="keyword-bubble-item" key={item}>
                  {item}
                  {index === element?.length - 1 ? '' : ', '}
                </span>
              );
            })}
          </Fragment>,
        );
      }
    }

    return (
      <Bubble
        arrowPointAtCenter
        dark
        closeOnScroll
        placement="top-start"
        className="keyword-bubble"
        content={elementList}
      >
        <span>{p}.</span>
      </Bubble>
    );
  };

  const getAllChildList = (
    selectKeyword: Array<any>,
    func?: (item: any) => void,
  ) => {
    return selectKeyword.reduce((pre, cur) => {
      func?.(cur);
      return [...pre, ...(cur.child || [])];
    }, []);
  };

  // 超过5个就展示...
  const keywordSelectInfo = useCallback(() => {
    // 选中一个就展示原始关键词, 选中多个，就展示多个合成关键词
    let total = 0;
    let keywordText = '';
    let keywordMap: { [key: string]: any } = {};
    if (selectKeyword.length === 1) {
      const item = selectKeyword[0];
      keywordMap = getClassKeywordList([...(item?.child || []), item]);
      total = item.count;
      keywordText = getKeywordText(
        uniqueArray(Object.values(keywordMap).flat(3)),
        5,
      );
    } else {
      let keywordList: Array<string> = [];
      const list = getAllChildList(selectKeyword, (cur) => {
        total += cur.count;
        keywordList.push(cur.keyword);
      });
      // 还是将子级导出
      keywordMap = getClassKeywordList(list);
      keywordText = getKeywordText(keywordList, 7);
    }

    const isSinge = total === 1;

    const formatMessageId = `{count} review${isSinge ? '' : 's'} ${
      isSinge ? 'is' : 'are'
    } selected, including`;
    return (
      <>
        {total < tableTotal ? (
          <span style={{ display: 'inline-flex' }}>
            {formatMessage({ id: 'Adding all relevant words' })}
            <BubbleCommon
              placement="top"
              tipsMap={selectedKeywordTipsText}
            ></BubbleCommon>{' '}
            ，
          </span>
        ) : null}
        {formatMessage(
          { id: formatMessageId },
          {
            count: tableTotal,
          },
        )}{' '}
        {bubble(keywordText, keywordMap)}
      </>
    );
  }, [selectKeyword, formatMessage, tableTotal]);

  const handleKeywordClick = (keyword: any) => {
    // 因为selectKeywordInfo顺序与selectKeyword顺序一致，所以能如此查看
    const index = selectKeywordInfo.indexOf(keyword.keyword);
    // 没有就是添加, 有就添加
    const localKeywords: any =
      index === -1
        ? [...selectKeyword, keyword]
        : selectKeyword.filter((_, i) => i !== index);
    setSelectKeyword(localKeywords);

    const value = getBackKeyword(localKeywords);
    keywordChange(value);
  };

  useEffect(() => {
    if (
      keywords &&
      Array.isArray(keywords) &&
      keywords.length > 0 &&
      autoSelectFirst
    ) {
      handleKeywordClick(keywords[0]);
    }
  }, [keywords]);

  const { Keyword_Color } = useGetSingePageSourceRule({
    keys: ['Keyword_Color'],
  });

  const typeClassMap: any = {
    1: 'dbo-keyword--green',
    2: 'dbo-keyword--red',
  };

  return (
    <div className="keyword-common-wrapper keywordbox-content">
      {tempKeyword?.map((keyword: any, index: number) => {
        const type = sentimentType(
          keyword.sentiment_rating,
          true,
          needBrandHealth,
        );
        return (
          // type 1 绿色positive 2 红色negative 3 灰色neutral
          // 不需要hover交互时 样式类名stabled
          // 选中时 样式类名is-selected
          <span
            key={index}
            onClick={() => {
              if (stabled) return;
              handleKeywordClick(keyword);
            }}
            className={`dbo-keyword ${Keyword_Color ? '' : typeClassMap[type]}${
              stabled ? ' stabled' : ''
            }${
              selectKeywordInfo.includes(keyword.keyword) ? ' is-selected' : ''
            }`}
          >
            {/* 如果有数字 样式类名strong-text */}
            {getTagText(keyword)}
            <p className="strong-text">
              {getShowCount(keyword?.mergedCount || keyword?.count)}
            </p>
          </span>
        );
      })}
      {selectKeyword.length > 0 && !commentLoading && (
        <p className="keywords-bottom-tips">{keywordSelectInfo()}</p>
      )}
    </div>
  );
}
